import { NavLink, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { getAllPosts } from "../lib/blog";

const CATEGORIES = [
  {
    label: "Financial Freedom",
    tags: ["financial-freedom", "offer-7", "taxes", "retirement", "insurance", "budgeting", "estate", "per-diem", "domicile", "business", "planning", "checklist", "education", "protection", "legal"],
    offerLink: "/offers/financial-freedom",
    offerLabel: "Wealth Program →",
  },
  {
    label: "Decision-Making",
    tags: ["decision-making", "offer-3", "psychology", "training", "FORDEC", "frameworks", "startle-response", "CRM", "communication", "NTSB", "safety", "weather", "bias", "fatigue", "wellness", "procedures", "briefing", "culture", "debriefing", "technology", "automation"],
    offerLink: "/offers/decision-mastery",
    offerLabel: "Decision Cohort →",
  },
  {
    label: "Career Pivot",
    tags: ["career-pivot", "offer-5", "resume", "interviews", "networking", "linkedin", "personal-brand", "compensation", "negotiation", "industries", "opportunity", "transition", "mindset", "military", "leadership", "strategy", "preparation", "mistakes"],
    offerLink: "/offers/career-pivot",
    offerLabel: "Career Program →",
  },
];

function categorizePosts() {
  const posts = getAllPosts();
  const categorized: { label: string; offerLink: string; offerLabel: string; posts: { slug: string; title: string }[] }[] = [];
  const assigned = new Set<string>();

  for (const cat of CATEGORIES) {
    const tagSet = new Set(cat.tags.map((t) => t.toLowerCase()));
    const matched = posts.filter((p) => {
      if (assigned.has(p.slug)) return false;
      return p.meta.tags?.some((t) => tagSet.has(t.toLowerCase()));
    });
    matched.forEach((p) => assigned.add(p.slug));
    categorized.push({
      label: cat.label,
      offerLink: cat.offerLink,
      offerLabel: cat.offerLabel,
      posts: matched.map((p) => ({ slug: p.slug, title: p.meta.title })),
    });
  }

  // Uncategorized
  const rest = posts.filter((p) => !assigned.has(p.slug));
  if (rest.length > 0) {
    categorized.push({
      label: "General",
      offerLink: "/blog",
      offerLabel: "All Articles →",
      posts: rest.map((p) => ({ slug: p.slug, title: p.meta.title })),
    });
  }

  return categorized;
}

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
];

function linkClass({ isActive }: { isActive: boolean }) {
  return [
    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-deck-panel text-signal-amber"
      : "text-slate-300 hover:bg-deck-panel/80 hover:text-white",
  ].join(" ");
}

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const categories = categorizePosts();

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-deck/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <NavLink to="/" className="font-display text-xl font-semibold tracking-tight text-white">
            Pilot Mastery Academy
          </NavLink>
          <span className="hidden rounded-full border border-white/15 px-2 py-0.5 text-xs text-slate-400 sm:inline">
            Professional pilots
          </span>
        </div>
        <nav className="flex flex-wrap items-center gap-1" aria-label="Primary">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
              {item.label}
            </NavLink>
          ))}
          {/* Journal mega menu trigger */}
          <div className="relative">
            <button
              ref={buttonRef}
              onClick={() => setMenuOpen((v) => !v)}
              className={[
                "rounded-md px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1",
                menuOpen
                  ? "bg-deck-panel text-signal-amber"
                  : "text-slate-300 hover:bg-deck-panel/80 hover:text-white",
              ].join(" ")}
            >
              Journal
              <svg
                className={`h-3.5 w-3.5 transition-transform ${menuOpen ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {menuOpen && (
              <div
                ref={menuRef}
                className="absolute right-0 top-full mt-2 w-[720px] max-w-[95vw] rounded-xl border border-white/10 bg-deck-deep shadow-lift backdrop-blur-md"
              >
                <div className="grid grid-cols-1 gap-0 divide-y divide-white/5 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  {categories.map((cat) => (
                    <div key={cat.label} className="p-4">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-signal-amber">
                        {cat.label}
                      </h3>
                      <ul className="mt-3 space-y-1">
                        {cat.posts.map((post) => (
                          <li key={post.slug}>
                            <Link
                              to={`/blog/${post.slug}`}
                              onClick={() => setMenuOpen(false)}
                              className="block rounded-md px-2 py-1.5 text-xs text-slate-400 hover:bg-deck-panel/60 hover:text-white transition-colors leading-snug"
                            >
                              {post.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={cat.offerLink}
                        onClick={() => setMenuOpen(false)}
                        className="mt-3 inline-block text-xs font-semibold text-signal-sky hover:text-sky-300"
                      >
                        {cat.offerLabel}
                      </Link>
                    </div>
                  ))}
                </div>
                <div className="border-t border-white/5 px-4 py-3">
                  <Link
                    to="/blog"
                    onClick={() => setMenuOpen(false)}
                    className="text-sm font-semibold text-signal-sky hover:text-sky-300"
                  >
                    View all articles →
                  </Link>
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>
      <div className="border-t border-white/5 bg-deck-deep/50">
        <div className="mx-auto flex max-w-6xl flex-wrap gap-1 px-4 py-2">
          <span className="w-full text-xs font-medium uppercase tracking-wider text-slate-500 sm:w-auto sm:pr-2">
            Offers
          </span>
          <NavLink to="/offers/financial-freedom" className={linkClass}>Wealth (#7)</NavLink>
          <NavLink to="/offers/decision-mastery" className={linkClass}>Decisions (#3)</NavLink>
          <NavLink to="/offers/career-pivot" className={linkClass}>Career (#5)</NavLink>
        </div>
      </div>
    </header>
  );
}
